"use client";

import { ArrowRightIcon } from "lucide-react";
import { useState } from "react";
import type { Address } from "viem";

import { TokenAvatar } from "@/components/TokenAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { SwapTransaction, SwapTransactionType } from "@/domain/types";
import { useSwapTransactions } from "@/hooks/useSwapTransactions";
import { addressUrl, formatAmount, formatTimeAgo, shortAddress, txUrl } from "@/lib/format";

const PAGE_SIZE = 10;

const TYPE_LABELS: Record<SwapTransactionType, string> = {
  swap: "Swap",
  "add-v2": "Add V2",
  "add-v3": "Add V3",
  "remove-v2": "Remove V2",
  "remove-v3": "Remove V3",
};

const TYPE_VARIANTS: Record<SwapTransactionType, "default" | "secondary" | "outline"> = {
  swap: "default",
  "add-v2": "secondary",
  "add-v3": "secondary",
  "remove-v2": "outline",
  "remove-v3": "outline",
};

function RowSkeleton({ showWallet }: { showWallet: boolean }) {
  return (
    <>
      {/* Mobile card skeleton */}
      <div className="flex flex-col gap-2 border-b border-border/60 py-3 sm:hidden">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-14 rounded-full" />
          <Skeleton className="h-3 w-10" />
        </div>
        <Skeleton className="h-4 w-40" />
        {showWallet && <Skeleton className="h-3 w-24" />}
      </div>
      {/* Desktop row skeleton */}
      <div className="hidden items-center justify-between gap-3 py-2.5 sm:flex">
        <Skeleton className="h-4 w-14" />
        <Skeleton className="h-5 w-14 rounded-full" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-20" />
        {showWallet && <Skeleton className="h-4 w-24" />}
      </div>
    </>
  );
}

function LegAmount({ leg }: { leg: SwapTransaction["legs"][number] }) {
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <TokenAvatar symbol={leg.token.symbol} address={leg.token.address} size={20} />
      <span className="truncate whitespace-nowrap">
        {formatAmount(BigInt(leg.amount), leg.token.decimals, 4)} {leg.token.symbol}
      </span>
    </div>
  );
}

function MobileCard({ tx, showWallet }: { tx: SwapTransaction; showWallet: boolean }) {
  const [legA, legB] = tx.legs;
  return (
    <div
      className="flex cursor-pointer flex-col gap-1.5 border-b border-border/60 py-3 text-sm last:border-b-0 hover:bg-muted/40 sm:hidden"
      onClick={() => window.open(txUrl(tx.hash), "_blank", "noopener,noreferrer")}
    >
      <div className="flex items-center justify-between">
        <Badge variant={TYPE_VARIANTS[tx.type]}>{TYPE_LABELS[tx.type]}</Badge>
        <span className="text-xs text-muted-foreground">{formatTimeAgo(tx.timestamp)}</span>
      </div>
      <div className="flex items-center gap-2 text-foreground">
        <div className="min-w-0 flex-1">{legA ? <LegAmount leg={legA} /> : "—"}</div>
        <ArrowRightIcon className="size-3.5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">{legB ? <LegAmount leg={legB} /> : "—"}</div>
      </div>
      {showWallet && (
        <a
          href={addressUrl(tx.wallet)}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-xs text-muted-foreground hover:text-foreground hover:underline"
        >
          {shortAddress(tx.wallet)} ↗
        </a>
      )}
    </div>
  );
}

/** Desktop row rendered as flat grid cells, wrapped in a `contents`-display
 * div (not a Fragment) so every cell is still a direct child of the ONE grid
 * in SwapTransactionsList for CSS Grid sizing purposes, while the wrapper
 * itself gives the row one shared hover/click target for opening the tx
 * page. Border-bottom goes on every cell (not the wrapper) since cells no
 * longer share a box to put it on. */
function DesktopRowCells({ tx, showWallet, isLast }: { tx: SwapTransaction; showWallet: boolean; isLast: boolean }) {
  const [legA, legB] = tx.legs;
  // -mx-1.5/px-1.5 extends each cell's box into half the grid's gap-x-3 on
  // both sides, so the hover background/border bridge the gap between cells
  // instead of showing a seam — cellClass alone can't do that since gap-x-3
  // lives on the container, outside any single cell's box.
  const cellClass = `hidden items-center py-2.5 text-sm sm:flex -mx-1.5 px-1.5 ${isLast ? "" : "border-b border-border/60"} group-hover:border-y group-hover:border-border group-hover:bg-muted/40`;
  return (
    <div className="contents group cursor-pointer" onClick={() => window.open(txUrl(tx.hash), "_blank", "noopener,noreferrer")}>
      <span className={`${cellClass} whitespace-nowrap text-xs text-muted-foreground group-hover:rounded-l-md group-hover:border-l`}>
        {formatTimeAgo(tx.timestamp)}
      </span>
      <div className={cellClass}>
        <Badge variant={TYPE_VARIANTS[tx.type]}>{TYPE_LABELS[tx.type]}</Badge>
      </div>
      <div className={`${cellClass} min-w-0 text-foreground`}>{legA ? <LegAmount leg={legA} /> : "—"}</div>
      <div className={`${cellClass} min-w-0 text-foreground ${showWallet ? "" : "group-hover:rounded-r-md group-hover:border-r"}`}>
        {legB ? <LegAmount leg={legB} /> : "—"}
      </div>
      {showWallet && (
        <a
          href={addressUrl(tx.wallet)}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className={`${cellClass} whitespace-nowrap text-xs text-muted-foreground hover:text-foreground hover:underline group-hover:rounded-r-md group-hover:border-r`}
        >
          {shortAddress(tx.wallet)} ↗
        </a>
      )}
    </div>
  );
}

/** Shared list for the global Explore → Transactions feed and a single
 * wallet's Portfolio → Activity feed — pass `wallet` to scope to it (and
 * drop the now-redundant Wallet column). Paginated 10 rows at a time over
 * every page fetched so far, requesting another upstream batch only once
 * the user pages past what's already loaded. Renders a stacked card per row
 * on mobile and one shared grid table from `sm` up. */
export function SwapTransactionsList({
  wallet,
  emptyMessage = "No activity yet.",
}: {
  wallet?: Address;
  emptyMessage?: string;
}) {
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useSwapTransactions(wallet);
  const [page, setPage] = useState(0);
  const allItems = data?.pages.flatMap((p) => p.items) ?? [];
  const items = allItems.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const showWallet = !wallet;
  const gridCols = showWallet ? "sm:grid-cols-[auto_auto_1fr_1fr_auto]" : "sm:grid-cols-[auto_auto_1fr_1fr]";

  const moreAvailable = (page + 1) * PAGE_SIZE < allItems.length || hasNextPage;

  async function goNext() {
    if ((page + 1) * PAGE_SIZE >= allItems.length && hasNextPage) {
      await fetchNextPage();
    }
    setPage((p) => p + 1);
  }

  if (isLoading) {
    return (
      <div>
        <RowSkeleton showWallet={showWallet} />
        <RowSkeleton showWallet={showWallet} />
        <RowSkeleton showWallet={showWallet} />
      </div>
    );
  }
  if (error) return <p className="text-sm text-destructive">Failed to load transactions.</p>;
  if (allItems.length === 0) return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;

  return (
    <div>
      <div className="overflow-x-auto">
        {/* Mobile cards */}
        <div className="sm:hidden">
          {items.map((tx) => (
            <MobileCard key={tx.hash} tx={tx} showWallet={showWallet} />
          ))}
        </div>

        {/* Desktop: header + every row are children of this ONE grid. */}
        <div className={`hidden gap-x-3 sm:grid ${gridCols}`}>
          <span className="text-xs text-muted-foreground">Time</span>
          <span className="text-xs text-muted-foreground">Type</span>
          <span className="text-xs text-muted-foreground">Token amount</span>
          <span className="text-xs text-muted-foreground">Token amount</span>
          {showWallet && <span className="text-xs text-muted-foreground">Wallet</span>}
          {items.map((tx, i) => (
            <DesktopRowCells key={tx.hash} tx={tx} showWallet={showWallet} isLast={i === items.length - 1} />
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
          ← Prev
        </Button>
        <span className="text-xs text-muted-foreground">Page {page + 1}</span>
        <Button variant="secondary" size="sm" onClick={goNext} disabled={!moreAvailable || isFetchingNextPage}>
          {isFetchingNextPage ? "Loading…" : "Next →"}
        </Button>
      </div>
    </div>
  );
}
